import React from 'react'
import { DefaultValues, FormProvider } from 'react-hook-form'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { makeStyles } from 'transfix-ui/core'
import { EditAs2CertificateModalForm } from '../../forms/EditAs2CertificateModalForm'
import { useEditAs2CertificateModalForm } from '../../forms/useEditAs2CertificateModalForm'
import { useAs2CertificateMetadataLazyQuery } from '../../graphql/As2CertificateMetadata'
import { IAs2CertificateSchema } from '../../schema/As2CertificateSchema'

interface IEditAs2CertificateModalProps extends Omit<IModalBaseProps, 'modalType'> {
  defaultValues: DefaultValues<IAs2CertificateSchema>
  onSubmit: (options: IAs2CertificateSchema) => void
  onSuccess: () => void
  error?: string | string[]
}

const useEditAs2CertificateModalStyles = makeStyles({
  root: {
    paddingRight: '20em',
  },
})

const getDefaultValues = (
  defaultValues: DefaultValues<IAs2CertificateSchema>
): DefaultValues<IAs2CertificateSchema> => {
  const { body = '', id = '', x509ReadError = null } = defaultValues || {}
  return {
    body,
    id,
    x509ReadError,
  }
}

export const EditAs2CertificateModal = ({
  defaultValues,
  onClose,
  onSubmit,
  onSuccess,
  open = false,
  ...props
}: IEditAs2CertificateModalProps) => {
  const currentValues = getDefaultValues(defaultValues)

  const methods = useEditAs2CertificateModalForm({
    defaultValues: { body: currentValues.body, id: currentValues.id! },
  })

  const [getMetaData, { data }] = useAs2CertificateMetadataLazyQuery()

  const { as2CertificateMetadata } = data || {}
  const { x509ReadError = currentValues.x509ReadError } = as2CertificateMetadata || {}

  const validateHandler = (certificateBody: string) => {
    getMetaData({
      variables: {
        body: certificateBody,
      },
    })
  }

  const { handleSubmit } = methods

  const submitHandler = handleSubmit((formParams: IAs2CertificateSchema) => {
    onSubmit(formParams)
    onClose()
    onSuccess()
  })

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: () => {
        onClose()
      },
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      action: submitHandler,
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]

  const classes = useEditAs2CertificateModalStyles()

  return (
    <ModalBase
      dataTest='EditAs2CertificateModal'
      modalType={MODAL_TYPES.form}
      size={4}
      open={open}
      actions={actions}
      className={classes.root}
      onClose={onClose}
      {...props}>
      <FormProvider {...methods}>
        <EditAs2CertificateModalForm validateHandler={validateHandler} error={x509ReadError} />
      </FormProvider>
    </ModalBase>
  )
}
